/**
 * Where the toast goes while a sheet is open (UI-audit a1-14).
 *
 * The toast is docked over the tab bar, z-index 40, above every sheet — so
 * with a sheet up it sat on the sheet's own controls: "Added to the Year"
 * over the More menu's "Not for me". Sheets are bottom-anchored, so there is
 * no bottom position that clears one. Two answers, and nothing in between:
 *
 * - **above** — the top sheet's top edge has room over it (the menu, a
 *   sheet at its lower snap): the toast sits just above that edge, on the
 *   scrim, where it covers nothing anyone can tap.
 * - **held** — the top sheet fills the screen: the toast is not drawn and
 *   its clock stops, and it shows for its remaining time once there is room
 *   (the sheet lowered or closed). Its Undo is still the undo for what was
 *   just done, so it is offered late rather than over a control, or lost.
 *
 * `dock` is everything else: no sheet open, exactly as before.
 *
 * Dependency-free, so the Worker's suite tests it (`test/toast-spot.test.ts`).
 */

export type ToastSpot =
  | { mode: 'dock' }
  | { mode: 'above'; top: number }
  | { mode: 'held' }

export const DOCK: ToastSpot = { mode: 'dock' }

export function toastSpot(
  /** The top-most visible sheet's top edge, viewport px; null for none. */
  sheetTop: number | null,
  /** The toast's own height. */
  height: number,
  { gap = 12, minTop = 8 }: { gap?: number; minTop?: number } = {},
): ToastSpot {
  if (sheetTop == null) return DOCK
  const top = Math.round(sheetTop - gap - height)
  return top >= minTop ? { mode: 'above', top } : { mode: 'held' }
}

export const sameSpot = (a: ToastSpot, b: ToastSpot): boolean =>
  a.mode === b.mode && (a.mode !== 'above' || a.top === (b as { top: number }).top)
